import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'

/**
 * Dashboard v2 preferences (V2, shared-first).
 *
 * The two user-tunable knobs the Lifecycle Command Center reads: which chart
 * treatment to render and whether to overlay per-bar counts. Theme is a
 * separate, older concern owned by ThemeContext — the Settings "Dashboard"
 * section surfaces all three together, but they persist independently.
 *
 * Persisted to localStorage (per-user, per-browser) to match how theme is
 * stored; these are purely presentational, so there's no need to round-trip
 * them through the backend AppSettings table.
 */

/** The locked v2 chart-type set (see epic_lifecycle_dashboard.md). */
export type DashboardChartType =
  | 'diverging' // default — adds/changes/creds up, removals down (net read)
  | 'area' // translucent non-additive area
  | 'grouped' // grouped columns
  | 'weekly' // weekly buckets + per-category labels
  | 'heatmap' // calendar heatmap

export const CHART_TYPE_OPTIONS: ReadonlyArray<{
  key: DashboardChartType
  label: string
  description: string
}> = [
  {
    key: 'diverging',
    label: 'Diverging columns',
    description: 'Additions above the axis, removals below — reads net growth vs churn.',
  },
  {
    key: 'area',
    label: 'Layered area',
    description: 'Translucent non-additive areas per category.',
  },
  {
    key: 'grouped',
    label: 'Grouped columns',
    description: 'Side-by-side bars per category, per day.',
  },
  {
    key: 'weekly',
    label: 'Weekly + labels',
    description: 'Weekly buckets with per-category counts shown.',
  },
  {
    key: 'heatmap',
    label: 'Calendar heatmap',
    description: 'Activity intensity per day on a calendar grid.',
  },
]

export const DEFAULT_CHART_TYPE: DashboardChartType = 'diverging'
export const DEFAULT_SHOW_VALUES = false

interface DashboardPrefsContextType {
  chartType: DashboardChartType
  setChartType: (t: DashboardChartType) => void
  showValues: boolean
  setShowValues: (v: boolean) => void
}

const DashboardPrefsContext = createContext<DashboardPrefsContextType | undefined>(
  undefined,
)

const CHART_TYPE_KEY = 'ssops-dashboard-chart-type'
const SHOW_VALUES_KEY = 'ssops-dashboard-show-values'

const VALID_CHART_TYPES = new Set<DashboardChartType>(
  CHART_TYPE_OPTIONS.map(o => o.key),
)

function readChartType(): DashboardChartType {
  const stored = localStorage.getItem(CHART_TYPE_KEY)
  return stored && VALID_CHART_TYPES.has(stored as DashboardChartType)
    ? (stored as DashboardChartType)
    : DEFAULT_CHART_TYPE
}

function readShowValues(): boolean {
  const stored = localStorage.getItem(SHOW_VALUES_KEY)
  return stored === null ? DEFAULT_SHOW_VALUES : stored === 'true'
}

interface DashboardPrefsProviderProps {
  children: ReactNode
}

export function DashboardPrefsProvider({ children }: DashboardPrefsProviderProps) {
  const [chartType, setChartTypeState] = useState<DashboardChartType>(() =>
    typeof window !== 'undefined' ? readChartType() : DEFAULT_CHART_TYPE,
  )
  const [showValues, setShowValuesState] = useState<boolean>(() =>
    typeof window !== 'undefined' ? readShowValues() : DEFAULT_SHOW_VALUES,
  )

  const setChartType = useCallback((t: DashboardChartType) => {
    setChartTypeState(t)
    localStorage.setItem(CHART_TYPE_KEY, t)
  }, [])

  const setShowValues = useCallback((v: boolean) => {
    setShowValuesState(v)
    localStorage.setItem(SHOW_VALUES_KEY, String(v))
  }, [])

  // Keep in sync across tabs/windows.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === CHART_TYPE_KEY) setChartTypeState(readChartType())
      if (e.key === SHOW_VALUES_KEY) setShowValuesState(readShowValues())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <DashboardPrefsContext.Provider
      value={{ chartType, setChartType, showValues, setShowValues }}
    >
      {children}
    </DashboardPrefsContext.Provider>
  )
}

export function useDashboardPrefs() {
  const context = useContext(DashboardPrefsContext)
  if (context === undefined) {
    throw new Error('useDashboardPrefs must be used within a DashboardPrefsProvider')
  }
  return context
}
